import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Divider, Modal } from "@mui/material";
import CloseMenu from "@assets/svg/closeMenu.svg?react";
import AirbnbLogo from "@assets/svg/AirbnbLogo.svg?react";
import BookingLogo from "@assets/svg/BookingLogo.svg?react";
import Hint from "@assets/svg/Hint.svg?react";
import { LoadingWrapper } from "@components";

import useBreakPoinst from "@hooks/useBreakPoinst";
import { useTranslation } from "react-i18next";
import {
  ModalWrapper,
  CloseButtonWrapper,
  CloseMenuIcon,
  CardContainer,
  ContentBox,
  Title,
  Description,
  ActionContainer,
  StyledButton,
  RowsContainer,
  ChannelHeaderRow,
  ChannelBox,
  ChannelLogoIcon,
  HintWrapper,
} from "./styles/ComparePropertiesModal";
import CompareRow1 from "./CompareRow1";
import { useAutomation, useProperty } from "@services";
import { ToastMessageContext } from "@context/toast-message-cotnext";

interface Props {
  open: boolean;
  onClose: () => void;
  handleNextModal: () => void;
  onRefetchOnboarding?: () => void;
}

const ComparePropertiesModal = ({ 
  open, 
  onClose, 
  handleNextModal, 
  onRefetchOnboarding 
}: Props) => {
  const [showHint, setShowHint] = useState(false);
  const [hotelIds, setHotelIds] = useState<{ [key: string]: string }>({});
  const [errorStates, setErrorStates] = useState<{ [key: string]: boolean }>(
    {}
  );
  const { showError } = useContext(ToastMessageContext);
  const navigate = useNavigate();
  const { isPhone } = useBreakPoinst();
  const { t } = useTranslation();
  const channexChannelId = localStorage.getItem("channexChannelId");
  const { connectAirbnbPropertyToBookingMutation, getAirbnbListingQuery } =
    useProperty();
  const { mutate, isPending, isSuccess } =
    connectAirbnbPropertyToBookingMutation({
      onSuccess: () => {
        onClose();
        handleNextModal();
      },
      onError: (e: any) => {
        showError(
          Array.isArray(e?.response?.data?.errorMessages)
            ? e?.response?.data?.errorMessages[0]?.messageEn
            : "رقم عقار غير صالح"
        );
      },
    });

  // Revalidate main onboarding query AFTER API is fulfilled
  useEffect(() => {
    if (isSuccess) {
      onRefetchOnboarding?.();
    }
  }, [isSuccess, onRefetchOnboarding]);

  const {
    data: propertyList,
    isLoading: airbnbLoading,
    isRefetching: airbnbRefreshing,
  } = getAirbnbListingQuery(channexChannelId || '');
  const list =
    propertyList?.response?.data?.listing_id_dictionary?.values || [];

  const connectedProperties = list.filter(
    (property: any) => property.isConncted === true
  );

  const handleHotelIdChange = (propertyId: string, hotelId: string) => {
    setHotelIds((prev) => ({
      ...prev,
      [propertyId]: hotelId,
    }));

    if (errorStates[propertyId]) {
      setErrorStates((prev) => ({
        ...prev,
        [propertyId]: false,
      }));
    }
  };

  const { GetListingManagmentQuery } = useAutomation();
  const { data, isLoading: isFetchingProperties } = GetListingManagmentQuery();
  const listingManagment = data?.result;

  const handleSubmit = () => {
    for (let i = 0; i < connectedProperties.length; i++) {
      const property = connectedProperties[i];
      const hotelId = hotelIds[property.id] || "";

      if (hotelId.trim() !== "") {
        const matchingProperty = listingManagment?.find(
          (item: any) => item.airbnbUnitId === property.id
        );

        const propertyIdToUse = matchingProperty?.propertyId || property.id;

        mutate(
          {
            hotelId: hotelId,
            propertyId: propertyIdToUse,
            channel: "BookingCom",
          },
          {
            onError: (error: any) => {
              setErrorStates((prev) => ({
                ...prev,
                [property.id]: true,
              }));
            },
          }
        );
      }
    }

    // onClose();
    // handleNextModal();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalWrapper $isPhone={isPhone}>
        <LoadingWrapper
          loading={
            airbnbLoading ||
            airbnbRefreshing ||
            isFetchingProperties ||
            isPending
          }
        >
          {/* <HintWrapper $isPhone={isPhone} /> Use styled component here */}
          <CloseButtonWrapper onClick={onClose} $isPhone={isPhone}>
            <CloseMenuIcon $isPhone={isPhone}>
              <CloseMenu />
            </CloseMenuIcon>
          </CloseButtonWrapper>
          <CardContainer $isPhone={isPhone}>
            <ContentBox $isPhone={isPhone}>
              <Title $isPhone={isPhone}>
                {t("onBoardingJourny.matchPropertiesTitle")}
              </Title>
              <Description $isPhone={isPhone}>
                {t("onBoardingJourny.matchPropertiesDescription")}
              </Description>
            </ContentBox>
          </CardContainer>
          <ChannelHeaderRow>
            <ChannelBox>
              <ChannelLogoIcon>
                <AirbnbLogo width={28} height={28} />
              </ChannelLogoIcon>
              Airbnb
            </ChannelBox>
            <ChannelBox>
              <ChannelLogoIcon>
                <BookingLogo width={28} height={28} />
              </ChannelLogoIcon>
              Booking.com
            </ChannelBox>
          </ChannelHeaderRow>
          <RowsContainer>
            {connectedProperties.map((property: any, idx: number) => (
              <CompareRow1
                key={property.id || idx}
                showText={idx === 0}
                image={property.image}
                title={property.title}
                hasSuccess={false}
                propertyId={property.id}
                hotelId={hotelIds[property.id] || ""}
                hasError={errorStates[property.id] || false}
                onHotelIdChange={(hotelId: string) =>
                  handleHotelIdChange(property.id, hotelId)
                }
              />
            ))}
          </RowsContainer>
          <ActionContainer $isPhone={isPhone}>
            <StyledButton
              onClick={handleSubmit}
              $TextColor="white"
              $BgColor="#5B3FFF"
              $isPhone={isPhone}
              $width="127px"
            >
              {t("onBoardingJourny.confirmConnection")}
            </StyledButton>
          </ActionContainer>
        </LoadingWrapper>
      </ModalWrapper>
    </Modal>
  );
};

export default ComparePropertiesModal;
